import { useEffect, useState, useCallback, createContext } from 'react';
import { routes, nonAuthenticatedRoutes } from '../../options/routes';

// API calls
import { getUserProfile, getClub, getClubLicences, getInvoices, getExpiredClubLicences, getClubPlayers } from '../../api';
import { getClubId, getManageClubId, getManageProfile } from '../../utils/helpers/clubHelper';

const BaseContext = createContext({});

const BaseProvider = ({ children }) =>
{
  const [clubProfiles, setClubProfiles] = useState(null);
  const [baseData, setBaseData] = useState({
    loading: true,
    userRole: '',
    userProfile: {},
    profiles: null,
    clubProfile: {},
    isManagingClub: false,
    activeRoute: '',
  });
  const [invoiceData, setInvoiceData] = useState({
    loading: false,
    failedInvoices: false,
    invoices: null
  });

  const getBaseData = useCallback(async () =>
  {

    // Get token, userProfile and clubProfile from local storage
    const activeRoute = window.location.pathname;
    let token, clubProfile;
    // Logout and redirect to login page if error parsing token or clubProfile
    try
    {
      token = JSON.parse(localStorage.getItem('token'));
      clubProfile = JSON.parse(localStorage.getItem('clubProfile'));
    } catch (error)
    {
      loginRedirect(activeRoute);
      return;
    }

    // Logout and redirect to login page if token or clubProfile not in local storage
    if (
      !token ||
      !(clubProfile && Object.keys(clubProfile).length)
    )
    {
      loginRedirect(activeRoute);
      return;
    }

    // API call to get user profile and update local storage data
    const data = await getUserProfile();
    if (!data || !data.success || !(data.data && (Object.keys(data.data).length)))
    {
      loginRedirect(activeRoute);
      return;
    }
    const manageClubId = await getManageClubId();
    let manageClubProfile = await getManageProfile();

    if (manageClubId && (!manageClubProfile || manageClubProfile?.id !== manageClubId))
    {
      // We need to fetch the manage club id and save to local storage
      const managedClubdata = await getClub(manageClubId);
      manageClubProfile = managedClubdata.data;
      localStorage.setItem('manageClubProfile', JSON.stringify(manageClubProfile));
    }
    const { role, user } = data.data;
    localStorage.setItem('userRole', JSON.stringify(role));
    localStorage.setItem('userProfile', JSON.stringify(user));

    // Redirect to home if trying to access any non-authenticated routes when logged in
    if (nonAuthenticatedRoutes.includes(activeRoute))
    {
      window.location.href = routes.home;
      return;
    }
    const clubId = await getClubId();
    const licences = await getClubLicences(clubId);
    const expiredLicences = await getExpiredClubLicences(clubId);

    const hasEliteLicence = licences?.data && licences?.data.length ?
      !!licences?.data.find((licence) => licence.code === 'index_personal' || licence.code === 'index_club') : false;
    const hasElitePersonalLicence = licences?.data && licences?.data.length ?
      !!licences?.data.find((licence) => licence.code === 'index_personal') : false;
    const hasEliteClubLicence = licences?.data && licences?.data.length ?
      !!licences?.data.find((licence) => licence.code === 'index_club') : false;
    const hasTrialLicence = licences?.data && licences?.data.length ?
      !!licences?.data.find((licence) => licence.code === 'trial_index_club') : false;
    const hasFullEliteLicense = licences?.data && licences?.data.length ?
      !!licences?.data.find((licence) => licence.code === 'elite') : false;

    const hasExpiredElitePersonalLicence = expiredLicences?.data && expiredLicences?.data.length ?
      !!licences?.data.find((licence) => licence.code === 'index_personal') : false;
    const hasExpiredEliteClubLicence = expiredLicences?.data && expiredLicences?.data.length ?
      !!licences?.data.find((licence) => licence.code === 'index_club') : false;

    let storedInvoiceData = localStorage.getItem("invoice_data");
    if (storedInvoiceData)
    {
      setInvoiceData(JSON.parse(storedInvoiceData));
    }
    else if (!invoiceData.invoices && !invoiceData.loading)
    {
      setInvoiceData({ loading: true });
      let invoices = [];
      let failedInvoices = false;
      const invoiceResult = await getInvoices(manageClubId ? manageClubId : clubProfile.id);
      Object.values(invoiceResult.data).forEach((groups) =>
      {

        groups.data.forEach((it) =>
        {
          if (it.paid === 'failed')
          {
            failedInvoices = true;
          }
          invoices.push(it);
        })

      })
      let newInvoiceData = {
        loading: false,
        invoices,
        failedInvoices
      };
      localStorage.setItem("invoice_data", JSON.stringify(newInvoiceData));
      setInvoiceData(newInvoiceData);
    }

    const hasRezzilPlayerLicence = licences?.data && licences?.data.length ? !!licences?.data.find((licence) => licence.code === 'squadspro') : false;
    // TODO: Should we redirect here if there are no active licences?
    let profiles = [];
    if (clubProfile)
    {
      let playerProfilesData = localStorage.getItem('clubProfiles');
      if (playerProfilesData)
      {
        profiles = JSON.parse(playerProfilesData);
      } else
      {
        playerProfilesData = await getClubPlayers(await getClubId());
        profiles = playerProfilesData.data.profiles;
        //      localStorage.setItem('clubProfiles', JSON.stringify(profiles));
      }

    }
    setBaseData({
      loading: false,
      userRole: role,
      userProfile: user,
      profiles,
      clubProfile: manageClubId ? manageClubProfile : clubProfile,
      isManagingClub: (manageClubId > 0),
      activeRoute,
      hasEliteLicence: hasEliteLicence,
      hasElitePersonalLicence: hasElitePersonalLicence,
      hasEliteClubLicence: hasEliteClubLicence,
      hasTrialLicence: hasTrialLicence,
      hasFullEliteLicense: hasFullEliteLicense,
      hasExpiredElitePersonalLicence: hasExpiredElitePersonalLicence,
      hasExpiredEliteClubLicence: hasExpiredEliteClubLicence,
      hasRezzilPlayerLicence: hasRezzilPlayerLicence,
      licences: licences?.data || [],
      expiredLicences: expiredLicences,
    });
  }, []);

  useEffect(() =>
  {

    getBaseData();
  }, [getBaseData]);

  const loginRedirect = (activeRoute) =>
  {
    if (!nonAuthenticatedRoutes.some(v => activeRoute.includes(v)))
    {
      window.location.href = routes.logout;
    }
  };

  return (
    <BaseContext.Provider
      value={{
        baseData,
        getBaseData,
        invoiceData
      }}
    >
      {children}
    </BaseContext.Provider>
  );
}

export { BaseProvider, BaseContext };

export default BaseProvider;
